import { Database, Users } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useTranslation } from 'react-i18next'
import type { Dataset } from '@/types/dataset'

interface DatasetSelectorProps {
  selectedDatasetId: string
  onDatasetChange: (id: string) => void
  datasets: Dataset[]
  selectedSymbol?: string
  onSymbolChange?: (symbol: string) => void
}

export default function DatasetSelector({
  selectedDatasetId,
  onDatasetChange,
  datasets,
  selectedSymbol,
  onSymbolChange
}: DatasetSelectorProps) {
  const { t } = useTranslation()
  const selectedDataset = datasets.find(d => d.id === selectedDatasetId)


  const isPartitioned = String((selectedDataset?.metadata as any)?.partitionStrategy) === 'by_symbol' ||
                        String((selectedDataset?.metadata as any)?.partitionType) === 'symbol'
  const availableSymbols = ((selectedDataset?.metadata as any)?.symbols as string[]) ||
                          ((selectedDataset?.metadata as any)?.syncMetadata?.symbols as string[]) || []


  return (
    <div className="flex items-center gap-2">
      <Database className="w-4 h-4 text-muted-foreground" />
      <Select value={selectedDatasetId} onValueChange={onDatasetChange}>
        <SelectTrigger className="w-[250px]">
          <SelectValue placeholder={t('scripts.selectDataset') || 'Select dataset'} />
        </SelectTrigger>
        <SelectContent>
          {datasets.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground">
              {t('scripts.noDatasetsAvailable') || 'No datasets available'}
            </div>
          ) : (
            datasets.map((dataset) => {
              const isDatasetPartitioned = dataset.metadata?.partitionStrategy === 'by_symbol' ||
                                         dataset.metadata?.partitionType === 'symbol'
              return (
                <SelectItem key={dataset.id} value={dataset.id}>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{dataset.name}</span>
                      {isDatasetPartitioned && (
                        <Users className="w-3 h-3 text-blue-500" />
                      )}
                    </div>
                    {dataset.rowCount != null && (
                      <span className="text-xs text-muted-foreground ml-2">
                        ({dataset.rowCount.toLocaleString()})
                      </span>
                    )}
                  </div>
                </SelectItem>
              )
            })
          )}
        </SelectContent>
      </Select>

      {/* Symbol selector for partitioned datasets */}
      {isPartitioned && onSymbolChange && (
        <>
          <Users className="w-4 h-4 text-muted-foreground" />
          <Select value={selectedSymbol || ''} onValueChange={onSymbolChange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Select symbol" />
            </SelectTrigger>
            <SelectContent>
              {availableSymbols.map((symbol) => (
                <SelectItem key={symbol} value={symbol}>
                  {symbol}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      {selectedDataset && (
        <span className="text-xs text-muted-foreground hidden sm:inline">
          {selectedDataset.rowCount != null && `${selectedDataset.rowCount.toLocaleString()} rows`}
          {isPartitioned && selectedSymbol && ` • ${selectedSymbol}`}
        </span>
      )}
    </div>
  )
}
